import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { EmptyState, Skeleton } from 'shared/components/ui/primitives'
import Loader from 'shared/components/ui/loader'
import { scannerApi, apiMessage, extractList } from 'shared/api/scanner'
import EventThumb from 'shared/components/EventThumb'
import { hasLimitedAccess } from 'shared/util/permissions'
import { useAuth } from 'context/AuthContext'
import { setSelectedEvent } from 'shared/util/storage'
import { cn } from 'shared/util/cn'
import loginHero from '../assets/login-hero.svg'

function eventParts(dateStr) {
  const date = new Date(dateStr)
  if (!dateStr || Number.isNaN(date.getTime())) return null
  return {
    day: date.toLocaleDateString('en-GB', { day: '2-digit' }),
    month: date.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase(),
    weekday: date.toLocaleDateString('en-GB', { weekday: 'short' }),
    time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  }
}

function venueOf(event) {
  return (
    event?.oVenue?.sName ||
    event?.oVenue?.sShortAddress ||
    event?.oVenue?.sAddress ||
    event?.sVenue ||
    event?.sLocation ||
    event?.sCity ||
    event?.sAddress ||
    ''
  )
}

function SearchIcon() {
  return (
    <svg className="h-5 w-5 text-dark-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3-3" />
    </svg>
  )
}

function PinIcon({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 21s7-5.4 7-11a7 7 0 1 0-14 0c0 5.6 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.2" />
    </svg>
  )
}

PinIcon.propTypes = { className: PropTypes.string }

function eventsEmptyCopy(tab, search) {
  const query = String(search || '').trim()
  if (query) {
    return {
      title: 'No matching events',
      message: `Nothing matches “${query}”. Try another event name.`
    }
  }
  if (tab === 'past') {
    return {
      title: 'No past events',
      message: 'Past events you have access to will appear here once they finish.'
    }
  }
  return {
    title: 'No upcoming events',
    message: 'You don’t have any upcoming events to scan. Ask your organiser to assign you to an event.'
  }
}

function Chevron({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
      <path d="m9 6 6 6-6 6" />
    </svg>
  )
}

Chevron.propTypes = { className: PropTypes.string }

function EventCard({ event, past, limited, featured, onOpen }) {
  const parts = eventParts(event.dStartDate)
  const venue = venueOf(event)

  if (featured) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="group flex w-full flex-col overflow-hidden rounded-3xl bg-[#3B1818] text-left shadow-card sm:rounded-[28px] md:flex-row md:items-stretch"
      >
        <div className="relative aspect-square w-full shrink-0 overflow-hidden bg-[#1A0808] md:w-[240px] lg:w-[280px] xl:w-[320px]">
          <EventThumb event={event} className="absolute inset-0 h-full w-full object-cover object-center" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-between p-4 sm:p-5 md:p-6 lg:p-8">
          <div className="flex items-start justify-between gap-2">
            <span className="rounded-full bg-theme px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-light sm:px-3 sm:py-1 sm:text-xxs">
              {past ? 'Completed' : 'Up next'}
            </span>
            {limited && (
              <span className="rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-bold uppercase text-dark sm:px-3 sm:py-1 sm:text-xxs">
                Limited
              </span>
            )}
          </div>
          <div className="mt-4 md:mt-6">
            {parts && (
              <p className="mb-2 inline-flex items-center rounded-full bg-light/10 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-light sm:mb-2.5 sm:px-3 sm:py-1.5 sm:text-sm">
                {parts.weekday} · {parts.day} {parts.month} · {parts.time}
              </p>
            )}
            <h3 className="mb-1.5 line-clamp-2 text-3xl font-bold leading-tight text-light sm:mb-2 sm:text-4xl md:text-5xl lg:text-6xl">
              {event.sTitle}
            </h3>
            {venue && (
              <p className="mb-4 flex items-center gap-1 text-xs font-medium text-light/85 sm:mb-5 sm:gap-1.5 sm:text-sm">
                <PinIcon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                <span className="line-clamp-1">{venue}</span>
              </p>
            )}
            <span className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-theme px-4 text-sm font-semibold text-light sm:h-12 sm:w-auto sm:justify-start sm:rounded-2xl sm:px-5 md:h-14 md:px-7 md:text-2xl lg:h-[3.75rem] lg:rounded-[18px] lg:px-8 lg:text-3xl">
              Open scanner
              <Chevron className="h-4 w-4 md:h-5 md:w-5 lg:h-6 lg:w-6" />
            </span>
          </div>
        </div>
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex min-h-[120px] w-full overflow-hidden rounded-2xl bg-surface text-left shadow-card transition hover:-translate-y-0.5 hover:shadow-lg sm:min-h-[148px] sm:rounded-[24px]"
    >
      <div className="relative w-[92px] shrink-0 self-stretch bg-dark-800 sm:w-[118px] md:w-[128px]">
        <EventThumb event={event} className="absolute inset-0 h-full w-full object-cover" />
        {parts && (
          <div className="absolute left-1.5 top-1.5 flex min-w-[44px] flex-col items-center rounded-lg bg-light/95 px-1.5 py-1 shadow-sm sm:left-2 sm:top-2 sm:min-w-[52px] sm:rounded-xl sm:px-2 sm:py-1.5">
            <span className="text-[9px] font-bold uppercase tracking-wide text-theme sm:text-[10px]">{parts.month}</span>
            <span className="text-3xl font-extrabold leading-none text-dark sm:text-4xl">{parts.day}</span>
          </div>
        )}
        {past && (
          <span className="absolute bottom-1.5 left-1.5 rounded-full bg-dark/75 px-1.5 py-0.5 text-[9px] font-bold uppercase text-light sm:bottom-2 sm:left-2 sm:px-2 sm:text-[10px]">
            Past
          </span>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-between p-2.5 sm:p-3.5 md:p-4">
        <div>
          <div className="mb-0.5 flex items-start justify-between gap-2 sm:mb-1">
            <h3 className="mb-0 line-clamp-2 text-2xl font-bold leading-snug text-dark sm:text-3xl lg:text-4xl">{event.sTitle}</h3>
            {limited && (
              <span className="shrink-0 rounded-full bg-primary-900 px-1.5 py-0.5 text-[9px] font-bold uppercase text-primary-200 sm:px-2 sm:py-1 sm:text-[10px]">
                Limited
              </span>
            )}
          </div>
          {parts && (
            <p className="mb-0.5 text-xs text-dark-400 sm:mb-1 sm:text-sm">
              {parts.weekday} · {parts.time}
            </p>
          )}
          {venue && (
            <p className="mb-0 flex items-center gap-1 text-[11px] text-muted sm:text-xs">
              <PinIcon className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" />
              <span className="line-clamp-1">{venue}</span>
            </p>
          )}
        </div>
        <div className="mt-2 flex items-center justify-between sm:mt-3">
          <span className="text-xs font-semibold text-theme sm:text-sm">Open scanner</span>
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-theme-900 text-theme transition group-hover:bg-theme group-hover:text-light sm:h-9 sm:w-9">
            <Chevron />
          </span>
        </div>
      </div>
    </button>
  )
}

EventCard.propTypes = {
  event: PropTypes.shape({
    _id: PropTypes.string,
    sTitle: PropTypes.string,
    dStartDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
  }).isRequired,
  past: PropTypes.bool,
  limited: PropTypes.bool,
  featured: PropTypes.bool,
  onOpen: PropTypes.func.isRequired
}

const EventListing = () => {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('upcoming')
  const [events, setEvents] = useState([])
  const [searchItem, setSearchItem] = useState('')
  const [debouncedValue, setDebouncedValue] = useState('')
  const [skip, setSkip] = useState(0)
  const [hasMoreData, setHasMoreData] = useState(true)
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const listRef = useRef(null)
  const navigate = useNavigate()
  const firstName = (user?.sName || 'Staff').split(' ')[0]

  const fetchEvents = useCallback(
    async (reset = false, nextSkip = 0) => {
      try {
        if (reset) setIsLoading(true)
        setError(null)
        const params = { sSearch: debouncedValue, nSkip: nextSkip, nLimit: 10 }
        const res = activeTab === 'upcoming' ? await scannerApi.eventsAll(params) : await scannerApi.eventsPast(params)
        const eventData = extractList(res, ['aEvent', 'aEvents'])
        setEvents((prev) => (reset ? eventData : [...prev, ...eventData]))
        setHasMoreData(eventData.length >= 10)
      } catch (err) {
        setError(apiMessage(err))
        if (reset) setEvents([])
      } finally {
        setIsLoading(false)
      }
    },
    [activeTab, debouncedValue]
  )

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(searchItem), 400)
    return () => clearTimeout(handler)
  }, [searchItem])

  useEffect(() => {
    setSkip(0)
    setHasMoreData(true)
    fetchEvents(true, 0)
  }, [fetchEvents])

  const onScroll = (e) => {
    if (!hasMoreData || isLoading) return
    const { scrollHeight, scrollTop, clientHeight } = e.target
    if (scrollHeight - scrollTop < clientHeight + 80) {
      const next = skip + 10
      setSkip(next)
      fetchEvents(false, next)
    }
  }

  const openEvent = (event) => {
    setSelectedEvent(event)
    navigate(`/events/${event._id}`)
  }

  const featured = !searchItem && activeTab === 'upcoming' && events[0] ? events[0] : null
  const rest = featured ? events.slice(1) : events
  const emptyCopy = eventsEmptyCopy(activeTab, debouncedValue)

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-[1320px] flex-1 flex-col bg-bg">
      <div className="px-3 pt-3 sm:px-4 sm:pt-4 md:px-6">
        <div className="relative mb-3 overflow-hidden rounded-2xl bg-[#3B1818] px-4 py-3.5 text-light sm:mb-4 sm:rounded-[28px] sm:px-5 sm:py-5 md:px-6 md:py-6">
          <img
            src={loginHero}
            alt=""
            className="pointer-events-none absolute -right-6 -top-8 h-28 w-28 opacity-[0.16] sm:-right-4 sm:-top-8 sm:h-40 sm:w-40 md:h-48 md:w-48"
          />
          <div className="pointer-events-none absolute -bottom-10 -left-8 h-20 w-20 rounded-full bg-theme/25 blur-2xl sm:h-28 sm:w-28" />
          <p className="relative mb-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-theme-700 sm:mb-1 sm:text-[11px] sm:tracking-[0.22em]">
            Gate Scanner
          </p>
          <h1 className="relative mb-0.5 text-3xl font-bold text-light sm:mb-1 sm:text-4xl md:text-5xl">Hi, {firstName}</h1>
          <p className="relative mb-0 text-xs text-light/70 sm:text-sm">Pick an event and start admitting guests.</p>
        </div>

        <div className="mb-2.5 flex rounded-xl bg-surface p-1 shadow-sm sm:mb-3 sm:rounded-2xl">
          {[
            { id: 'upcoming', label: 'Upcoming' },
            { id: 'past', label: 'Past' }
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'h-10 flex-1 rounded-lg text-xs font-semibold sm:h-12 sm:rounded-xl sm:text-sm',
                activeTab === tab.id ? 'bg-theme text-light shadow-glow' : 'text-dark-400'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="relative mb-3 sm:mb-4">
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center sm:left-4">
            <SearchIcon />
          </span>
          <input
            type="search"
            value={searchItem}
            onChange={(e) => setSearchItem(e.target.value)}
            placeholder="Search events"
            className="h-10 w-full rounded-xl border-0 bg-surface pl-10 pr-3 text-sm text-dark shadow-sm outline-none placeholder:text-dark-600 focus:ring-1 focus:ring-theme sm:h-12 sm:rounded-2xl sm:pl-12 sm:pr-4 sm:text-base"
          />
        </div>
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto px-3 pb-6 sm:px-4 sm:pb-8 md:px-6" onScroll={onScroll}>
        {isLoading && events.length === 0
          ? (
          <div className="space-y-3">
            <div className="flex flex-col overflow-hidden rounded-3xl sm:rounded-[28px] md:flex-row">
              <Skeleton className="aspect-square w-full rounded-none md:w-[240px] md:shrink-0 lg:w-[280px]" />
              <Skeleton className="h-40 w-full rounded-none md:h-[240px] md:flex-1 lg:h-[280px]" />
            </div>
            <div className="grid gap-2.5 sm:gap-3 md:grid-cols-2">
              <Skeleton className="h-28 rounded-2xl sm:h-36 sm:rounded-[24px]" />
              <Skeleton className="h-28 rounded-2xl sm:h-36 sm:rounded-[24px]" />
            </div>
          </div>
            )
          : error
            ? (
          <EmptyState title="Couldn’t load events" message={error} />
              )
            : events.length === 0
              ? (
          <div className="rounded-2xl bg-surface px-4 py-10 text-center shadow-card sm:rounded-[28px] sm:px-6 sm:py-14">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-theme-900 text-2xl sm:mb-4 sm:h-16 sm:w-16 sm:text-3xl" aria-hidden="true">
              🎟️
            </div>
            <h3 className="mb-1.5 block text-2xl font-bold leading-snug text-ink sm:mb-2 sm:text-3xl">{emptyCopy.title}</h3>
            <p className="mx-auto mb-0 max-w-[22rem] text-sm leading-relaxed text-muted">{emptyCopy.message}</p>
          </div>
                )
              : (
          <div className="space-y-3 sm:space-y-4">
            {featured && (
              <EventCard
                event={featured}
                featured
                past={activeTab === 'past'}
                limited={hasLimitedAccess(user, featured._id)}
                onOpen={() => openEvent(featured)}
              />
            )}
            {rest.length > 0 && (
              <>
                {featured && (
                  <p className="mb-0 pt-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted sm:pt-1 sm:text-[11px] sm:tracking-[0.18em]">
                    More events
                  </p>
                )}
                <div className="grid gap-2.5 sm:gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {rest.map((event) => (
                    <EventCard
                      key={event._id}
                      event={event}
                      past={activeTab === 'past'}
                      limited={hasLimitedAccess(user, event._id)}
                      onOpen={() => openEvent(event)}
                    />
                  ))}
                </div>
              </>
            )}
            {isLoading && (
              <div className="flex justify-center py-4">
                <Loader />
              </div>
            )}
          </div>
                )}
      </div>
    </div>
  )
}

export default EventListing
